import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTime(ts: number) {
  return new Date(ts * 1000).toLocaleString("zh-CN");
}

export function formatDuration(ms: number) {
  if (!ms) return "-";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} 秒`;
  return `${Math.floor(s / 60)} 分 ${s % 60} 秒`;
}

export function today() {
  return new Date().toLocaleDateString("sv-SE"); // YYYY-MM-DD
}

export const OUTCOME_OPTIONS = [
  "success",
  "reject_not_interested",
  "reject_busy",
  "reject_wrong_person",
  "reject_other",
] as const;

export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("sv-SE");
}

export interface BizMetrics {
  total: number;
  answered: number;
  completed: number;
  success: number;
  answerRate: number;
  completeRate: number;
  successRate: number;
}

export function aggregateTrend(days: { total: number; answered: number; completed: number; success: number }[]): BizMetrics {
  let total = 0, answered = 0, completed = 0, success = 0;
  for (const d of days) {
    total += d.total;
    answered += d.answered;
    completed += d.completed;
    success += d.success;
  }
  return {
    total, answered, completed, success,
    answerRate: total > 0 ? (answered / total) * 100 : 0,
    completeRate: answered > 0 ? (completed / answered) * 100 : 0,
    successRate: answered > 0 ? (success / answered) * 100 : 0,
  };
}

export function parseStringArray(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return JSON.parse(raw) as string[];
}

export const parseTags = parseStringArray;
export const parseIntentDirections = parseStringArray;

export function scoreTextColor(score: number | null | undefined): string {
  if (score == null) return "text-muted-foreground";
  if (score >= 0.7) return "text-green-600";
  if (score >= 0.4) return "text-yellow-600";
  return "text-red-600";
}

export function scoreBgColor(score: number | null | undefined): string {
  if (score == null) return "bg-muted";
  if (score >= 0.7) return "bg-green-500";
  if (score >= 0.4) return "bg-yellow-500";
  return "bg-red-500";
}

export function scorePercent(score: number | null | undefined): string {
  if (score == null) return "-";
  return (score * 100).toFixed(0);
}

export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  if (phone.length !== 11) return phone;
  return `${phone.slice(0, 3)}****${phone.slice(7)}`;
}

export function formatPct(num: number, den: number): string {
  if (den === 0) return "\u2014";
  return `${((num / den) * 100).toFixed(1)}%`;
}

export function relativeTime(ts: number): string {
  const diff = Date.now() / 1000 - ts;
  if (diff < 60) return "刚刚";
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`;
  return `${Math.floor(diff / 86400)} 天前`;
}

export type DatePreset = "today" | "yesterday" | "7d" | "30d" | null;

export const DATE_PRESETS: { key: DatePreset; label: string; range: () => [string, string] }[] = [
  { key: "today", label: "今天", range: () => [today(), today()] },
  { key: "yesterday", label: "昨天", range: () => [daysAgo(1), daysAgo(1)] },
  { key: "7d", label: "近 7 天", range: () => [daysAgo(6), today()] },
  { key: "30d", label: "近 30 天", range: () => [daysAgo(29), today()] },
];

export const DIM_LABELS: Record<string, string> = {
  slot_completion: "信息收集",
  compliance: "合规性",
  naturalness: "自然度",
  objection_handling: "异议处理",
  goal_achievement: "目标达成",
  topic_convergence: "话题收敛",
};

export const SOURCE_LABELS: Record<string, string> = {
  call: "电话外呼",
  outbound: "外呼",
  freeswitch: "电话对话",
  batch: "批量外呼",
  api: "API 调用",
  eval: "评测",
  "sandbox:text-chat": "文字聊天",
  "sandbox:text-audio": "文字转语音",
  "sandbox:voice-call": "语音对话",
};

export const BATCH_STATUS_COLORS: Record<string, string> = {
  running: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  cancelled: "bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200",
  pending: "bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200",
  interrupted: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
};

export function getBatchCapabilities(status: string) {
  return {
    canPause: status === "running",
    canResume: status === "paused",
    canContinue: status === "interrupted",
    canCancel: status === "running" || status === "paused" || status === "pending" || status === "interrupted",
  };
}

export function b64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
